import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Paper,
  Box,
  FormLabel,
  Button,
  Typography,
  TextField,
  Modal,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  InputAdornment,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormHelperText,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Tune as TuneIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import moment from "moment";

// --- Imports for Date Picker ---
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

import {
  updateDocumentField,
  updateLineItem,
  addLineItem,
  removeLineItem,
  saveDocument,
  processDocument,
  rejectDocument,
  archiveDocument,
  clearValidationErrors,
  setValidationErrors,
  clearErrors,
  deleteLineItemFromServer,
} from "../../../store/slices/documentProcessingSlice";
import SnackBarDialog from "../../../components/shared/SnackBarDialog";
import axiosDocumentProcessing from '../../../services/utility/axios-document-processing';

const ProcessButton = styled(LoadingButton)(({ theme }) => ({
  color: theme.palette.getContrastText("#673ab7"),
  backgroundColor: "#673ab7",
  "&:hover": {
    backgroundColor: "#5e35b1",
  },
}));

const StyledModal = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "98%",
  maxWidth: "98vw",
  backgroundColor: theme.palette.background.paper,
  boxShadow: 24,
  padding: 0,
  borderRadius: 1,
  display: "flex",
  flexDirection: "column",
  maxHeight: "96vh",
  overflow: "hidden",
  minHeight: "70vh",
  // Validation styles
  "& .MuiTextField-root": {
    "& .MuiInput-root.Mui-error:after": {
      borderBottom: "2px solid #d32f2f",
    },
    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
      border: "2px solid #d32f2f",
    },
    "& .MuiInput-underline.Mui-error:after": {
      borderBottomColor: "#d32f2f",
      borderBottomWidth: 2,
      transform: "scaleX(1)",
    },
    "& .MuiInput-root.Mui-error.Mui-focused:after": {
      borderBottomColor: "#d32f2f",
    },
    "& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline":
    {
      borderColor: "#d32f2f",
    },
  },

  "& .MuiFormHelperText-root.Mui-error": {
    color: "#d32f2f",
    fontSize: "0.75rem",
    marginTop: 3,
    marginLeft: 0,
    fontWeight: 400,
  },

  "& .MuiTableCell-root .MuiFormHelperText-root.Mui-error": {
    fontSize: "0.7rem",
    marginTop: 2,
    position: "absolute",
    whiteSpace: "nowrap",
    zIndex: 1,
    background: "white",
    padding: "0 4px",
    borderRadius: 2,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
  },

  "& .validation-summary": {
    backgroundColor: "#ffebee",
    border: "1px solid #ffcdd2",
    borderRadius: 4,
    padding: 12,
    marginBottom: 16,
    "& .MuiTypography-root": {
      color: "#c62828",
      fontWeight: 500,
    },
  },

  // Error shake animation
  "@keyframes errorShake": {
    "0%, 20%, 40%, 60%, 80%": { transform: "translateX(0)" },
    "10%": { transform: "translateX(-2px)" },
    "30%": { transform: "translateX(2px)" },
    "50%": { transform: "translateX(-2px)" },
    "70%": { transform: "translateX(2px)" },
  },

  "& .error-shake": {
    animation: "errorShake 0.5s ease-in-out",
  },

  "& .items_table td": {
    padding: "5px 4px",
    borderBottom: "none",
  },

  "& .items_table td input": {
    padding: "0 4px",
  },

  "& .items_table td .items_product_name": {
    width: "100%",
  },
}));

// Helper function to extract a readable filename from a URL
const getFileNameFromUrl = (url) => {
  if (!url) return "document.pdf";
  try {
    const urlPath = new URL(url).pathname;
    const decodedPath = decodeURIComponent(urlPath);
    return decodedPath.substring(decodedPath.lastIndexOf("/") + 1);
  } catch (e) {
    return "document.pdf";
  }
};

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// A VAT field is blank only when it is an empty / whitespace string.
// null and undefined display as "0" via `?? "0"`, so they count as a valid 0.
const isVatFieldBlank = (v) => typeof v === "string" && v.trim() === "";

// Reject Confirmation Modal Component
const RejectConfirmationModal = ({ open, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState("");

  const handleSave = () => {
    if (reason.trim()) {
      onConfirm(reason.trim());
      setReason(""); // Clear the reason after confirming
    }
  };

  const handleCancel = () => {
    setReason(""); // Clear the reason when canceling
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontSize: "1.25rem",
          fontWeight: 600,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        Are you sure, you want to Reject?
        <IconButton
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "grey.500",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 1, fontWeight: 600, pt: 2 }}>Reason</FormLabel>
          <TextField
            multiline
            rows={4}
            placeholder="Enter reason for Rejection."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 80 }}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          disabled={!reason.trim()}
          loading={loading}
          sx={{
            minWidth: 80,
            backgroundColor: "#B253AE",
            "&:hover": {
              backgroundColor: "#9a4796",
            },
          }}
        >
          Reject
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

// Delete Line Item Confirmation Modal Component
const DeleteLineItemConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  loading,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontSize: "1.25rem",
          fontWeight: 600,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        Are you sure, you want to delete?
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography sx={{ pt: 2 }}>
          This line item will be permanently deleted. This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
        <LoadingButton
          onClick={onConfirm}
          variant="contained"
          loading={loading}
          sx={{ minWidth: 80 }}
        >
          Yes
        </LoadingButton>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 80 }}
        >
          No
        </Button>
      </DialogActions>
    </Dialog>
  );
};
const SPCLookupCell = ({ spcValue, productName, productId, vatValue, lineVatValue, onSelect, disabled, error }) => {
  const [inputValue, setInputValue] = useState(spcValue || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInputValue(spcValue || "");
  }, [spcValue]);

  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setOptions([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axiosDocumentProcessing.get(
          `/invoice/products?boProductId=${encodeURIComponent(inputValue)}`,
          { signal: controller.signal }
        );
        setOptions(data || []);
      } catch (err) {
        if (!controller.signal.aborted) setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [inputValue]);

  // Rebuild the selected option so the field shows the SPC code, not the name
  const selectedOption =
    productId && spcValue
      ? { id: productId, name: productName, brandOwnerProductId: spcValue }
      : null;

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      loading={loading}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      onChange={(_, newValue) => {
        if (newValue && typeof newValue === "object") {
          onSelect({
            supplierProductCode: newValue.brandOwnerProductId || inputValue,
            productId: newValue.id,
            productName: newValue.name,
            vatValue: newValue.vatCode || 0,
            lineVatValue: newValue.LineVatCode || 0,
          });
        } else {
          // X / clear button
          setInputValue("");
          setOptions([]);
          onSelect({ supplierProductCode: "", productId: null, productName: "", vatValue: 0, lineVatValue: 0 });
        }
      }}
      getOptionLabel={(option) =>
        typeof option === "string"
          ? option
          : option.brandOwnerProductId || option.name || ""
      }
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      filterOptions={(x) => x}
      noOptionsText={inputValue.length < 2 ? "Type at least 2 characters..." : "No products found"}
      loadingText="Searching products..."
      sx={{
        // minWidth: 140,
        "& .MuiInput-root.MuiInputBase-sizeSmall .MuiInput-input": { padding: 0 },
        "& .MuiAutocomplete-inputRoot": { paddingY: 0 },
        "& .MuiAutocomplete-endAdornment": { top: "calc(50% - 4px)" },
      }}
      componentsProps={{
        popper: { placement: "bottom-start", sx: { minWidth: 350 } },
        paper: {
          sx: {
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            mt: 0.5,
          },
        },
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id} style={{ padding: "8px 12px", borderBottom: "1px solid #f0f0f0", fontSize: "0.85rem" }}>
          <Box>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>{option.name}</Typography>
            <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
              ID: {option.id}
              {option.brandOwnerProductId ? `  •  SPC: ${option.brandOwnerProductId}` : ""}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder="Enter SPC"
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
function EditViewDocumentProcess({ onShow, onHide }) {
  const dispatch = useDispatch();
  const { document, status, error, lastAction, validationErrors } = useSelector(
    (state) => state.documentProcessingSlice,
  );

  const [snackBar, setSnackBar] = useState({ text: "", type: "", show: false });

  // Local state to track the current reason/remarks to display
  const [currentReason, setCurrentReason] = useState("");

  const [expanded, setExpanded] = useState([
    "companyDetails",
    "recordDetails",
    "itemDetails",
  ]);
  const [editingCell, setEditingCell] = useState(null);
  const [lastEditedVatField, setLastEditedVatField] = useState(null);
  const [localAmounts, setLocalAmounts] = useState({
    subtotal: "",
    vat: "",
    shipping: "",
    total: "",
  });

  // State for reject confirmation modal
  const [showRejectModal, setShowRejectModal] = useState(false);

  // State for delete line item confirmation modal
  const [showDeleteLineItemModal, setShowDeleteLineItemModal] = useState(false);
  const [lineItemToDelete, setLineItemToDelete] = useState(null);

  // Add this helper function to check if a line item is manually added
  const isManuallyAddedLineItem = (lineItem) => {
    return lineItem.id && lineItem.id.toString().startsWith("new_");
  };

  // Helper function for formatting
  const formatToDecimalString = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  const to2dpStr = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : "";
  };

  // --- STATUS-BASED PERMISSION HELPERS ---
  const getDocumentStatus = () => {
    return document?.status?.toUpperCase() || "UNKNOWN";
  };

  const getDocumentReason = () => {
    // return document?.remarks || "";
    return currentReason || "";
  };

  const getDocumentArchiveStatus = () => {
    return document?.archived || "";
  };

  const isEditable = () => {
    const docStatus = getDocumentStatus();
    return docStatus === "FAIL";
  };

  const getVisibleButtons = () => {
    const docStatus = getDocumentStatus();
    const isArchived = getDocumentArchiveStatus();
    switch (docStatus) {
      case "FAIL":
        return { reject: true, save: true, process: true, archive: false };
      case "REJECTED":
        return { reject: false, save: false, process: false, archive: true };
      case "PASS":
        if (isArchived === true) {
          return { reject: false, save: false, process: false, archive: false };
        } else {
          return { reject: false, save: false, process: false, archive: true };
        }
      case "ARCHIVED":
        return { reject: false, save: false, process: false, archive: false };
      default:
        return { reject: false, save: false, process: false, archive: false };
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    const newExpanded = [...expanded];
    const index = newExpanded.indexOf(panel);
    if (index > -1) {
      newExpanded.splice(index, 1);
    } else {
      newExpanded.push(panel);
    }
    setExpanded(newExpanded);
  };

  // --- VALIDATION LOGIC ---
  const validateForm = () => {
    // Only validate if editable
    if (!isEditable()) return {};

    const errors = {};

    // Company Details validation
    // if (
    //   !document.from?.company_name ||
    //   document.from.company_name.trim() === ""
    // ) {
    //   errors["from.company_name"] = "Company Name is required";
    // }

    // if (!document.from?.email || document.from.email.trim() === "") {
    //   errors["from.email"] = "Company Email is required";
    // } else if (!emailRegex.test(document.from.email)) {
    //   errors["from.email"] = "Please enter a valid email address";
    // }

    // if (!document.from?.address || document.from.address.trim() === "") {
    //   errors["from.address"] = "Company Address is required";
    // }

    if (!document.supplier_member_account_code || document.supplier_member_account_code.trim() === "") {
      errors["supplier_member_account_code"] = "SMA Code is required";
    }

    // Validate only manually added line items
    const lineItemErrors = [];
    document.invoice_lines?.forEach((item, index) => {
      // if (!isManuallyAddedLineItem(item)) return; // Skip API-loaded rows

      const itemErrors = {};

      // SPC and Product are required only for manually added rows.
      if (isManuallyAddedLineItem(item)) {
        if (!item.supplier_product_code || item.supplier_product_code.trim() === "") {
          itemErrors.supplier_product_code = "SPC is required";
        }

        if (!item.product || !item.product_name || item.product_name.trim() === "") {
          itemErrors.product_name = "Product is required";
        }
      }

      // Line VAT and VAT must not be blank on any editable row.
      if (isVatFieldBlank(item.line_vat_code)) {
        itemErrors.line_vat_code = "Line VAT is required";
      }

      if (isVatFieldBlank(item.vat_code)) {
        itemErrors.vat_code = "VAT is required";
      }

      if (Object.keys(itemErrors).length > 0) {
        lineItemErrors[index] = itemErrors;
      }
    });

    if (lineItemErrors.length > 0) {
      errors["invoice_lines"] = lineItemErrors;
    }

    // Record Details validation
    // if (!document.invoice_number || document.invoice_number.trim() === "") {
    //   errors["invoice_number"] = "Invoice Number is required";
    // }

    // if (
    //   !document.credit_note_number ||
    //   document.credit_note_number.trim() === ""
    // ) {
    //   errors["credit_note_number"] = "Credit Note Number is required";
    // }

    // if (!document.order_number || document.order_number.trim() === "") {
    //   errors["order_number"] = "Order Number is required";
    // }

    // if (!document.invoice_date) {
    //   errors["invoice_date"] = "Date is required";
    // }

    // Line Items validation
    // const lineItemErrors = [];
    // document.invoice_lines?.forEach((item, index) => {
    //   const itemErrors = {};

    //   if (!item.quantity || parseFloat(item.quantity) <= 0) {
    //     itemErrors.quantity = "Quantity must be greater than 0";
    //   }

    //   if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
    //     itemErrors.unit_price = "Unit price must be greater than 0";
    //   }

    //   if (!item.product_name || item.product_name.trim() === "") {
    //     itemErrors.product_name = "Product info is required";
    //   }

    //   if (Object.keys(itemErrors).length > 0) {
    //     lineItemErrors[index] = itemErrors;
    //   }
    // });

    // if (lineItemErrors.length > 0) {
    //   errors["invoice_lines"] = lineItemErrors;
    // }

    // Check if we have at least one line item
    // if (!document.invoice_lines || document.invoice_lines.length === 0) {
    //   errors["invoice_lines_empty"] = "At least one line item is required";
    // }

    return errors;
  };

  const isFieldInError = (fieldPath) => {
    return validationErrors && validationErrors[fieldPath];
  };

  const getFieldError = (fieldPath) => {
    return validationErrors && validationErrors[fieldPath];
  };

  const isLineItemFieldInError = (index, field) => {
    return (
      validationErrors &&
      validationErrors["invoice_lines"] &&
      validationErrors["invoice_lines"][index] &&
      validationErrors["invoice_lines"][index][field]
    );
  };

  const getLineItemFieldError = (index, field) => {
    return (
      validationErrors &&
      validationErrors["invoice_lines"] &&
      validationErrors["invoice_lines"][index] &&
      validationErrors["invoice_lines"][index][field]
    );
  };

  // VAT lookup table for price reference
  const VAT_DATA = [
    { code: "S", externalCode: "1", description: "Standard", rate: 20 },
    { code: "E", externalCode: "0", description: "Exempt", rate: 0 },
    { code: "Z", externalCode: "0", description: "Zero", rate: 0 },
    { code: "R", externalCode: "2", description: "Reduced Rate", rate: 5 },
    { code: "O", externalCode: "0", description: "Out of Scope", rate: 0 },
    { code: "O2", externalCode: "O2", description: "Out of Scope", rate: 0 },
    { code: "V", externalCode: "0", description: "Vat Code", rate: 0 },
    { code: "T", externalCode: "1", description: "15%", rate: 15 },
    { code: "W", externalCode: "6", description: "Standard 17.5%", rate: 17.5 },
    { code: "L", externalCode: "L", description: "8%", rate: 8 },
    { code: "M", externalCode: "M", description: "5%", rate: 5 },
    { code: "A", externalCode: "A", description: "0", rate: 0 },
    { code: "B", externalCode: "B", description: "20%", rate: 20 },
  ];

  // Initialize currentReason when document loads
  useEffect(() => {
    if (document?.remarks) {
      setCurrentReason(document.remarks);
    } else {
      setCurrentReason(""); // Clear if no remarks
    }
  }, [document?.id]); // Only run when document ID changes (new document loaded)

  // --- EFFECT FOR AUTOMATIC CALCULATIONS ---
  // useEffect(() => {
  //     if (!document || !document.invoice_lines) return;
  //     let newSubtotal = 0, newVatTotal = 0;
  //     document.invoice_lines.forEach((item, index) => {
  //             // --- Keep the normalization logic as is ---
  //             const normalizedQty = to2dpStr(item.quantity);
  //             const normalizedUp = to2dpStr(item.unit_price);
  //             const isQActive = editingCell && editingCell.row === index && editingCell.key === 'quantity';
  //             const isUpActive = editingCell && editingCell.row === index && editingCell.key === 'unit_price';
  //             if (normalizedQty !== '' && item.quantity !== normalizedQty && !isQActive) {
  //                 dispatch(updateLineItem({ index, key: 'quantity', value: normalizedQty }));
  //             }
  //             if (normalizedUp !== '' && item.unit_price !== normalizedUp && !isUpActive) {
  //                 dispatch(updateLineItem({ index, key: 'unit_price', value: normalizedUp }));
  //             }

  //             // --- Core calculation variables ---
  //             const quantity = parseFloat(item.quantity) || 0;
  //             const price = parseFloat(item.unit_price) || 0;
  //             const netTotal = quantity * price;

  //             let vatPercent = parseFloat(item.line_vat_code) || 0;
  //             let vatValue = parseFloat(item.vat_value) || 0;

  //             if (item.vat_code !== null) {
  //                 let match = VAT_DATA.find(item => item.code === item.vat_code);
  //                 if (match) {
  //                     vatPercent = match.rate;
  //                 } else {
  //                     const externalMatches = VAT_DATA.filter(item => item.externalCode === item.vat_code);
  //                           if (externalMatches.length > 1) {
  //                             // Handle ambiguity: default to the first match
  //                             vatPercent = externalMatches[0].rate;
  //                         } else if (externalMatches.length === 1) {
  //                             // Single match by external code
  //                             vatPercent = externalMatches[0].rate;
  //                         } else {
  //                             // No match found at all
  //                             vatPercent = 0;
  //                         }
  //                 }
  //             } else {

  //                 if (item.line_vat_code !== null && item.line_vat_code !== "0.0000") {
  //                     const input = item.line_vat_code.toUpperCase();
  //                     let match = VAT_DATA.find(item => item.code === input);
  //                     if (match) {
  //                         vatPercent = match.rate;
  //                     } else {
  //                         const externalMatches = VAT_DATA.filter(item => item.externalCode === item.line_vat_code);
  //                             if (externalMatches.length > 1) {
  //                                 // Handle ambiguity: default to the first match
  //                                 vatPercent = externalMatches[0].rate;
  //                             } else if (externalMatches.length === 1) {
  //                                 // Single match by external code
  //                                 vatPercent = externalMatches[0].rate;
  //                             } else {
  //                                 // No match found at all
  //                                 vatPercent = 0;
  //                             }
  //                     }
  //                 }
  //             }

  //             // --- NEW VAT CALCULATION LOGIC ---
  //             const wasPercentLastEdited = lastEditedVatField?.index === index && lastEditedVatField?.field === 'percent';
  //             const wasValueLastEdited = lastEditedVatField?.index === index && lastEditedVatField?.field === 'value';

  //             if (wasPercentLastEdited) {
  //                 // If VAT % was the last field edited, it is the source of truth.
  //                 vatValue = netTotal * (vatPercent / 100);
  //             } else if (wasValueLastEdited) {
  //                 // If VAT Value was the last field edited, it is the source of truth.
  //                 // Recalculate the percentage based on the value.
  //                 vatPercent = netTotal > 0 ? (vatValue / netTotal) * 100 : 0;
  //             } else {
  //                 // Default behavior on initial load (or if neither has been touched)
  //                 // Prioritize calculating from percentage if it exists.
  //                 if (vatPercent > 0) {
  //                     // If a percentage exists in the data, trust it and calculate the value.
  //                     vatValue = netTotal * (vatPercent / 100);
  //                 } else if (netTotal > 0 && vatValue > 0) {
  //                     // IMPORTANT: If percent is 0 but a value exists in the data,
  //                     // trust the value and calculate the percentage from it.
  //                     vatPercent = (vatValue / netTotal) * 100;
  //                 } else {
  //                     // If both are zero or cannot be calculated, default to zero.
  //                     vatValue = 0;
  //                 }
  //             }
  //             // --- END OF NEW VAT LOGIC ---

  //             const lineTotal = netTotal + vatValue;

  //             // --- Dispatch updates if values have changed ---
  //             // Using toFixed to avoid floating point comparison issues
  //             if (parseFloat(item.net_total).toFixed(2) !== netTotal.toFixed(2)) {
  //                 dispatch(updateLineItem({ index, key: 'net_total', value: netTotal }));
  //             }
  //             if (parseFloat(item.line_total).toFixed(2) !== lineTotal.toFixed(2)) {
  //                 dispatch(updateLineItem({ index, key: 'line_total', value: lineTotal }));
  //             }
  //             // Check both VAT fields to ensure they are in sync
  //             if (parseFloat(item.vat_percent).toFixed(2) !== vatPercent.toFixed(2)) {
  //                 dispatch(updateLineItem({ index, key: 'vat_percent', value: vatPercent.toFixed(2) }));
  //             }
  //             if (parseFloat(item.vat_value).toFixed(2) !== vatValue.toFixed(2)) {
  //                 dispatch(updateLineItem({ index, key: 'vat_value', value: vatValue.toFixed(2) }));
  //             }

  //             // --- Update document totals ---
  //             newSubtotal += netTotal;
  //             newVatTotal += vatValue;
  //         });

  //         // --- Keep the total amount calculation as is ---
  //         const deliveryCharge = parseFloat(document.amount?.shipping) || 0;
  //         const newTotalAmount = newSubtotal + newVatTotal + deliveryCharge;
  //         if(document.amount?.subtotal !== newSubtotal) dispatch(updateDocumentField({ section: 'amount', key: 'subtotal', value: newSubtotal }));
  //         if(document.amount?.vat !== newVatTotal) dispatch(updateDocumentField({ section: 'amount', key: 'vat', value: newVatTotal }));
  //         if(document.amount?.total_including_vat_tax_discounts !== newTotalAmount) dispatch(updateDocumentField({ section: 'amount', key: 'total_including_vat_tax_discounts', value: newTotalAmount }));
  //     // document.invoice_lines.forEach((item, index) => {
  //     //     const normalizedQty = to2dpStr(item.quantity);
  //     //     const normalizedUp = to2dpStr(item.unit_price);
  //     //     const isQActive = editingCell && editingCell.row === index && editingCell.key === 'quantity';
  //     //     const isUpActive = editingCell && editingCell.row === index && editingCell.key === 'unit_price';
  //     //     if (normalizedQty !== '' && item.quantity !== normalizedQty && !isQActive) {
  //     //         dispatch(updateLineItem({ index, key: 'quantity', value: normalizedQty }));
  //     //     }
  //     //     if (normalizedUp !== '' && item.unit_price !== normalizedUp && !isUpActive) {
  //     //         dispatch(updateLineItem({ index, key: 'unit_price', value: normalizedUp }));
  //     //     }
  //     //     const quantity = parseFloat(item.quantity) || 0;
  //     //     const price = parseFloat(item.unit_price) || 0;
  //     //     const vatPercent = parseFloat(item.vat_percent) || 0;
  //     //     const vatVal = parseFloat(item.vat_value) || 0;
  //     //     const vatValue = vatVal !== 0 && vatPercent === 0 ? vatVal : (quantity * price) * (vatPercent / 100);
  //     //     const lineTotal = (quantity * price) + vatValue;
  //     //     const lineTotalOff = quantity * price;
  //     //     const netTotal = quantity * price;
  //     //     if (item.net_total !== netTotal) dispatch(updateLineItem({ index, key: 'net_total', value: netTotal }));
  //     //     if (item.line_total !== lineTotal) dispatch(updateLineItem({ index, key: 'line_total', value: lineTotal }));
  //     //     if (item.vat_percent !== vatPercent) dispatch(updateLineItem({ index, key: 'vat_percent', value: vatPercent }));
  //     //     if (item.vat_value !== vatValue) dispatch(updateLineItem({ index, key: 'vat_value', value: vatValue }));
  //     //     newSubtotal += lineTotalOff;
  //     //     newVatTotal += vatValue;
  //     // });
  //     // const deliveryCharge = parseFloat(document.amount?.shipping) || 0;
  //     // const newTotalAmount = newSubtotal + newVatTotal + deliveryCharge;
  //     // if(document.amount?.subtotal !== newSubtotal) dispatch(updateDocumentField({ section: 'amount', key: 'subtotal', value: newSubtotal }));
  //     // if(document.amount?.vat !== newVatTotal) dispatch(updateDocumentField({ section: 'amount', key: 'vat', value: newVatTotal }));
  //     // if(document.amount?.total_including_vat_tax_discounts !== newTotalAmount) dispatch(updateDocumentField({ section: 'amount', key: 'total_including_vat_tax_discounts', value: newTotalAmount }));
  // }, [document?.invoice_lines, document?.amount?.shipping, dispatch, editingCell, lastEditedVatField]);
  useEffect(() => {
    if (!document || !document.invoice_lines) return;

    document.invoice_lines.forEach((item, index) => {
      // --- Keep the normalization logic as is ---
      const normalizedQty = to2dpStr(item.quantity);
      const normalizedUp = to2dpStr(item.unit_price);
      const normalizedVc =
        item.vat_code === null ? null : to2dpStr(item.vat_code);
      const normalizedLvc =
        item.line_vat_code === null
          ? null
          : typeof item.line_vat_code === "string"
            ? item.line_vat_code
            : to2dpStr(item.line_vat_code);
      const normalizedVv = to2dpStr(item.vat_value);
      const normalizedLt = to2dpStr(item.line_total);
      const isQActive =
        editingCell &&
        editingCell.row === index &&
        editingCell.key === "quantity";
      const isUpActive =
        editingCell &&
        editingCell.row === index &&
        editingCell.key === "unit_price";
      const isVcActive =
        editingCell &&
        editingCell.row === index &&
        editingCell.key === "vat_code";
      const isLvcActive =
        editingCell &&
        editingCell.row === index &&
        editingCell.key === "line_vat_code";
      const isVvActive =
        editingCell &&
        editingCell.row === index &&
        editingCell.key === "vat_value";
      const isLtActive =
        editingCell &&
        editingCell.row === index &&
        editingCell.key === "line_total";
      if (
        normalizedQty !== "" &&
        item.quantity !== normalizedQty &&
        !isQActive
      ) {
        dispatch(
          updateLineItem({ index, key: "quantity", value: normalizedQty }),
        );
      }
      if (
        normalizedUp !== "" &&
        item.unit_price !== normalizedUp &&
        !isUpActive
      ) {
        dispatch(
          updateLineItem({ index, key: "unit_price", value: normalizedUp }),
        );
      }
      if (
        normalizedVc !== "" &&
        item.vat_code !== normalizedVc &&
        !isVcActive
      ) {
        dispatch(
          updateLineItem({ index, key: "vat_code", value: normalizedVc }),
        );
      }
      if (
        normalizedLvc !== "" &&
        item.line_vat_code !== normalizedLvc &&
        !isLvcActive
      ) {
        dispatch(
          updateLineItem({ index, key: "line_vat_code", value: normalizedLvc }),
        );
      }
      if (
        normalizedVv !== "" &&
        item.vat_value !== normalizedVv &&
        !isVvActive
      ) {
        dispatch(
          updateLineItem({ index, key: "vat_value", value: normalizedVv }),
        );
      }
      if (
        normalizedLt !== "" &&
        item.line_total !== normalizedLt &&
        !isLtActive
      ) {
        dispatch(
          updateLineItem({ index, key: "line_total", value: normalizedLt }),
        );
      }
    });
  }, [
    document?.invoice_lines,
    document?.amount?.shipping,
    dispatch,
    editingCell,
    lastEditedVatField,
  ]);

  // --- EFFECT FOR HANDLING API ACTION FEEDBACK ---
  useEffect(() => {
    if (status === "succeeded" && lastAction) {
      const successMessages = {
        save: "Record saved successfully!",
        process: "Record processed successfully!",
        reject: "Record rejected successfully!",
        archive: "Record archived successfully!",
      };
      setSnackBar({
        show: true,
        text: successMessages[lastAction],
        type: "success",
      });
      if (lastAction !== "save") {
        setTimeout(() => onHide(), 1500);
      }
    } else if (status === "failed" && error) {
      // Update currentReason with the error message
      setCurrentReason(error);
      setSnackBar({ show: true, text: error, type: "error" });
    }
  }, [status, error, lastAction, onHide]);

  // Clear validation errors when modal opens
  useEffect(() => {
    if (onShow) {
      dispatch(clearValidationErrors());
    }
  }, [onShow, dispatch]);

  // Add this useEffect to sync Redux values to local state when document loads/changes:
  useEffect(() => {
    if (document?.amount) {
      setLocalAmounts({
        subtotal: formatToDecimalString(document.amount.subtotal),
        vat: formatToDecimalString(document.amount.vat),
        shipping: formatToDecimalString(document.amount.shipping),
        total: formatToDecimalString(
          document.amount.total_including_vat_tax_discounts,
        ),
      });
    }
  }, [document?.id]); // Only sync when document ID changes (new document loaded)

  // --- FIELD HANDLERS WITH VALIDATION ERROR CLEARING ---
  const handleFieldChange = (section, key) => (event) => {
    if (!isEditable()) return; // Don't allow changes if not editable

    const { value } = event.target;
    dispatch(updateDocumentField({ section, key, value }));
    dispatch(clearValidationErrors());
    dispatch(clearErrors());
  };

  const handleDateChange = (section, key) => (newDate) => {
    if (!isEditable()) return; // Don't allow changes if not editable

    dispatch(
      updateDocumentField({
        section,
        key,
        value: newDate ? moment(newDate).toISOString() : null,
      }),
    );
    dispatch(clearValidationErrors());
    dispatch(clearErrors());
  };

  const handleLineItemChange = (index, key) => (event) => {
    if (!isEditable()) return; // Don't allow changes if not editable

    const { value } = event.target;
    dispatch(updateLineItem({ index, key, value }));
    dispatch(clearValidationErrors());
  };

  // --- ACTION BUTTON HANDLERS WITH VALIDATION ---
  const handleSave = () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      dispatch(setValidationErrors(errors));
      setSnackBar({
        show: true,
        text: "Please fix the validation errors before saving.",
        type: "error",
      });
      return;
    }

    dispatch(clearValidationErrors());
    dispatch(saveDocument(document));
  };

  const handleProcess = () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      dispatch(setValidationErrors(errors));
      setSnackBar({
        show: true,
        text: "Please fix the validation errors before processing.",
        type: "error",
      });
      return;
    }

    dispatch(clearValidationErrors());
    dispatch(processDocument(document));
  };

  // Updated reject handler to show confirmation modal
  const handleReject = () => {
    setShowRejectModal(true);
  };

  // New function to handle actual rejection with remarks
  const handleRejectConfirm = (remarks) => {
    dispatch(
      rejectDocument({
        documentId: document.id,
        remarks,
      }),
    );
    setShowRejectModal(false);
  };

  const handleRejectCancel = () => {
    setShowRejectModal(false);
  };

  const handleArchive = () => dispatch(archiveDocument(document.id));

  const handleAddLineItem = () => {
    if (!isEditable()) return; // Don't allow changes if not editable

    dispatch(addLineItem());
    dispatch(clearValidationErrors());
  };

  const handleRemoveLineItem = (index) => {
    if (!isEditable()) return; // Don't allow changes if not editable

    const lineItem = document.invoice_lines[index];
    setLineItemToDelete({ index, item: lineItem });
    setShowDeleteLineItemModal(true);
  };

  const handleDeleteLineItemConfirm = async () => {
    if (!lineItemToDelete) return;

    const { index, item } = lineItemToDelete;

    try {
      if (isManuallyAddedLineItem(item)) {
        // For manually added items, just remove locally
        dispatch(removeLineItem({ index }));
        dispatch(clearValidationErrors());
      } else {
        // For API-populated items, call the server first
        await dispatch(
          deleteLineItemFromServer({
            invoiceId: document.id,
            lineItemId: item.id,
            index,
          }),
        ).unwrap();
        dispatch(clearValidationErrors());
      }

      setShowDeleteLineItemModal(false);
      setLineItemToDelete(null);
    } catch (error) {
      // Error handling is done in the slice, but you could show additional feedback here
      console.error("Failed to delete line item:", error);
    }
  };

  const handleDeleteLineItemCancel = () => {
    setShowDeleteLineItemModal(false);
    setLineItemToDelete(null);
  };

  // --- RENDER LOGIC ---
  if (status === "loading" && !document) {
    return (
      <Modal open={onShow}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      </Modal>
    );
  }

  if (!document) {
    return (
      <Modal open={onShow} onClose={onHide}>
        <Box
          sx={{ p: 4, bgcolor: "background.paper", margin: "auto", mt: "20%" }}
        >
          No document data loaded.
        </Box>
      </Modal>
    );
  }

  const isLoading = status === "loading";
  const documentIdentifier =
    document.invoice_number || document.order_number || document.id;
  const visibleButtons = getVisibleButtons();
  const editable = isEditable();

  const handleExportCSV = () => {
    if (
      !document ||
      !document.invoice_lines ||
      document.invoice_lines.length === 0
    ) {
      setSnackBar({ show: true, text: "No items to export.", type: "warning" });
      return;
    }

    // Helper to format values for CSV (Escapes commas/quotes)
    const escapeCsvValue = (val) => {
      if (val === null || val === undefined) return "";
      const stringVal = String(val);
      if (stringVal.search(/("|,|\n)/g) >= 0) {
        return `"${stringVal.replace(/"/g, '""')}"`;
      }
      return stringVal;
    };

    const csvRows = [];

    // SUMMARY SECTION (The Parent Row Data)

    // Specific rows for the top of the file
    const summaryData = [
      ["Document Summary"], // Title
      ["Ref. Number", document.invoice_number ? document.invoice_number : ""],
      [
        "Union Name",
        document.to?.company_name ? document.to?.company_name : "",
      ],
      [
        "Supplier Name",
        document.from?.company_name ? document.from?.company_name : "",
      ],
      [
        "Date",
        document.invoice_date
          ? moment(document.invoice_date).format("DD/MM/YYYY")
          : "",
      ],
      [
        "Total Amount",
        document.amount?.total_including_vat_tax_discounts
          ? `${parseFloat(
            document.amount.total_including_vat_tax_discounts,
          ).toFixed(2)}`
          : "",
      ],
      ["Status", document.status ? document.status : ""],
      ["Reason", document.remarks ? document.remarks : ""],
      [], // Empty row for spacing
    ];

    // Push summary rows to CSV
    summaryData.forEach((infoRow) => {
      // join with comma, escaping values
      csvRows.push(infoRow.map(escapeCsvValue).join(","));
    });

    // Define Headers
    const headers = [
      "Line No",
      "Product Name",
      "Quantity",
      "Unit Price",
      "VAT Code",
      "VAT Value",
      "Line Total",
    ];

    // Push header row
    csvRows.push(headers.map(escapeCsvValue).join(","));

    // 4. DATA ROWS SECTION
    if (document.invoice_lines && Array.isArray(document.invoice_lines)) {
      document.invoice_lines.forEach((item, index) => {
        // Logic to match your table's VAT Code display logic
        const vatCodeDisplay =
          item.vat_code === null ? item.line_vat_code : item.vat_code;

        const rowValues = [
          item.line_number || index + 1,
          item.product_name,
          item.quantity,
          item.unit_price,
          vatCodeDisplay,
          item.vat_value,
          item.line_total,
        ];

        // Escape values, join them, and push to the master array
        csvRows.push(rowValues.map(escapeCsvValue).join(","));
      });
    }

    const csvString = csvRows.join("\n");

    // Trigger Download
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    const fileName = `ref_${document.invoice_number || "export"}_Items.csv`;

    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Modal open={onShow} onClose={onHide}>
        <StyledModal>
          <Box
            sx={{
              padding: "10px 16px 10px 16px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              borderBottom: "1px solid #e0e0e0",
              gap: "12px",
              width: "calc(100% - 32px)",
            }}
          >
            <Typography variant="h6" component="h6" sx={{ fontWeight: "bold" }}>
              #{documentIdentifier}
            </Typography>
            <Alert
              sx={{ padding: "0 16px", maxHeight: "40px" }}
              severity={
                getDocumentStatus() === "FAIL"
                  ? "error"
                  : getDocumentStatus() === "PASS"
                    ? "success"
                    : getDocumentStatus() === "REJECT"
                      ? "warning"
                      : "info"
              }
            >
              <AlertTitle sx={{ margin: "0", padding: "0" }}>
                <strong>{getDocumentStatus()}</strong>
              </AlertTitle>
            </Alert>
            {getDocumentReason() ? (
              <>
                <Alert
                  sx={{
                    flex: "1",
                    alignItems: "center",
                    padding: "2px 16px",
                    minHeight: "40px",
                  }}
                  severity={
                    getDocumentStatus() === "FAIL"
                      ? "error"
                      : getDocumentStatus() === "PASS"
                        ? "success"
                        : getDocumentStatus() === "REJECT"
                          ? "warning"
                          : "info"
                  }
                  icon={false}
                >
                  <p>{getDocumentReason()}</p>
                </Alert>{" "}
                <Box sx={{ textAlign: "right" }}>
                  <IconButton onClick={onHide}>
                    <CloseIcon />
                  </IconButton>
                </Box>{" "}
              </>
            ) : (
              <Box sx={{ textAlign: "right", flex: "1" }}>
                <IconButton onClick={onHide}>
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
            <Box
              sx={{
                width: "55%",
                padding: "0px 10px 16px 16px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box flexGrow={1}>
                {/* --- RECORD DETAILS ACCORDION --- */}
                <Accordion
                  expanded={expanded.includes("recordDetails")}
                  onChange={handleAccordionChange("recordDetails")}
                  sx={{ boxShadow: "none", "&:before": { display: "none" } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      px: 0,
                      minHeight: "48px",
                      "&.Mui-expanded": { minHeight: "48px" },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontSize: "1.1rem", fontWeight: "600" }}
                    >
                      Record Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 2, pb: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <FormLabel>
                            Ref. Number
                            {/* {document.invoice_number !== ""
                              ? "Invoice Number"
                              : "Credit Note Number"} */}
                          </FormLabel>
                          <TextField
                            fullWidth
                            size="small"
                            value={document.invoice_number || ""}
                            onChange={handleFieldChange(null, "invoice_number")}
                            // error={!!isFieldInError('invoice_number')}
                            disabled={!editable}
                          />
                          {/* {isFieldInError('invoice_number') && (
                                                        <FormHelperText error>{getFieldError('invoice_number')}</FormHelperText>
                                                    )} */}
                        </FormControl>
                      </Grid>
                      {/* <Grid item xs={12} sm={4}>
                                                <FormControl fullWidth>
                                                    <FormLabel>Credit Note Number</FormLabel>
                                                    <TextField 
                                                        fullWidth 
                                                        size="small" 
                                                        value={document.credit_note_number || ''} 
                                                        onChange={handleFieldChange(null, 'credit_note_number')}
                                                        error={!!isFieldInError('credit_note_number')}
                                                        disabled={!editable}
                                                    />
                                                    {isFieldInError('credit_note_number') && (
                                                        <FormHelperText error>{getFieldError('credit_note_number')}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            </Grid> */}
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <FormLabel>Order Number</FormLabel>
                          <TextField
                            fullWidth
                            size="small"
                            value={document.order_number || ""}
                            onChange={handleFieldChange(null, "order_number")}
                            // error={!!isFieldInError('order_number')}
                            disabled={!editable}
                          />
                          {/* {isFieldInError('order_number') && (
                                                        <FormHelperText error>{getFieldError('order_number')}</FormHelperText>
                                                    )} */}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <FormLabel>Date</FormLabel>
                          <DesktopDatePicker
                            value={
                              document.invoice_date
                                ? moment(document.invoice_date)
                                : null
                            }
                            onChange={handleDateChange(null, "invoice_date")}
                            disabled={!editable}
                            inputFormat="DD-MM-YYYY"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                size="small"
                              // error={!!isFieldInError('invoice_date')}
                              />
                            )}
                          />
                          {/* {isFieldInError('invoice_date') && (
                                                        <FormHelperText error>{getFieldError('invoice_date')}</FormHelperText>
                                                    )} */}
                        </FormControl>
                      </Grid>
                      {/* <Grid item xs={12} sm={4}>
                                                <FormControl fullWidth>
                                                    <FormLabel>Due Date</FormLabel>
                                                    <DesktopDatePicker
                                                        value={document.due_date ? moment(document.due_date) : null}
                                                        onChange={handleDateChange(null, 'due_date')}
                                                        disabled={!editable}
                                                        inputFormat="DD-MM-YYYY"
                                                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                                                    />
                                                </FormControl>
                                            </Grid> */}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
                {/* --- COMPANY DETAILS ACCORDION --- */}
                <Accordion
                  expanded={expanded.includes("companyDetails")}
                  onChange={handleAccordionChange("companyDetails")}
                  sx={{ boxShadow: "none", "&:before": { display: "none" } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      px: 0,
                      minHeight: "48px",
                      "&.Mui-expanded": { minHeight: "48px" },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontSize: "1.1rem", fontWeight: "600" }}
                    >
                      Company Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 2, pb: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <FormLabel>Company Name</FormLabel>
                          <TextField
                            fullWidth
                            size="small"
                            value={document.from?.company_name || ""}
                            onChange={handleFieldChange("from", "company_name")}
                            // error={!!isFieldInError('from.company_name')}
                            disabled={!editable}
                          />
                          {/* {isFieldInError('from.company_name') && (
                                                        <FormHelperText error>{getFieldError('from.company_name')}</FormHelperText>
                                                    )} */}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <FormLabel>Company Email</FormLabel>
                          <TextField
                            fullWidth
                            size="small"
                            value={document.from?.email || ""}
                            onChange={handleFieldChange("from", "email")}
                            // error={!!isFieldInError('from.email')}
                            disabled={!editable}
                          />
                          {/* {isFieldInError('from.email') && (
                                                        <FormHelperText error>{getFieldError('from.email')}</FormHelperText>
                                                    )} */}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <FormLabel title="Supplier Member Account Code">
                            Supplier Member Account
                          </FormLabel>
                          <TextField
                            fullWidth
                            size="small"
                            value={document.supplier_member_account_code || ""}
                            onChange={handleFieldChange(
                              null,
                              "supplier_member_account_code",
                            )}
                            error={!!isFieldInError("supplier_member_account_code")}
                            disabled={!editable}
                          />
                          {isFieldInError("supplier_member_account_code") && (
                            <FormHelperText error>
                              {getFieldError("supplier_member_account_code")}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* --- ITEM DETAILS ACCORDION --- */}
                <Accordion
                  expanded={expanded.includes("itemDetails")}
                  onChange={handleAccordionChange("itemDetails")}
                  sx={{ boxShadow: "none", "&:before": { display: "none" } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      px: 0,
                      minHeight: "48px",
                      "&.Mui-expanded": { minHeight: "48px" },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontSize: "1.1rem", fontWeight: "600" }}
                    >
                      Item Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 2, pb: 2 }}>
                    {validationErrors &&
                      validationErrors["invoice_lines_empty"] && (
                        <Box mb={2}>
                          <Typography color="error" variant="body2">
                            {validationErrors["invoice_lines_empty"]}
                          </Typography>
                        </Box>
                      )}
                    <Button
                      variant="contained"
                      disabled={
                        document.invoice_lines.length > 0 ? false : true
                      }
                      color="secondary"
                      size="small"
                      onClick={handleExportCSV}
                      startIcon={<SendIcon />}
                      sx={{ marginBottom: "10px" }}
                    >
                      Export as Excel
                    </Button>
                    <TableContainer
                      component={Paper}
                      sx={{ boxShadow: "none", border: "1px solid #e0e0e0", paddingBottom: "20px" }}
                    >
                      <Table size="small" className="items_table">
                        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableRow>
                            {[
                              "#",
                              "SPC",
                              "Product Info",
                              "QTY",
                              "Unit Price",
                              "Line VAT",
                              "VAT",
                              "VAT Value",
                              "Line Total",
                              editable ? "🗑" : "",
                            ]
                              .filter(Boolean)
                              .map((headCell, index) => (
                                <TableCell
                                  key={index}
                                  sx={{
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                    padding: "6px 10px"
                                  }}
                                >
                                  {headCell}
                                </TableCell>
                              ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {document.invoice_lines.map((item, index) => (
                            <TableRow key={item.id}>
                              <TableCell sx={{ textAlign: "center" }}>
                                {item.line_number || ""}
                              </TableCell>
                              <TableCell>
                                {isManuallyAddedLineItem(item) ? (
                                  <>
                                    <SPCLookupCell
                                      spcValue={item.supplier_product_code}
                                      productName={item.product_name}
                                      productId={item.product}
                                      disabled={!editable}
                                      error={!!isLineItemFieldInError(index, "supplier_product_code")}
                                      onSelect={({ supplierProductCode, productId, productName, vatValue, lineVatValue }) => {
                                        dispatch(updateLineItem({ index, key: "supplier_product_code", value: supplierProductCode }));
                                        dispatch(updateLineItem({ index, key: "product", value: productId ? String(productId) : null }));
                                        dispatch(updateLineItem({ index, key: "product_name", value: productName }));
                                        dispatch(updateLineItem({ index, key: "line_vat_code", value: lineVatValue }));
                                        dispatch(updateLineItem({ index, key: "vat_code", value: vatValue }));
                                        dispatch(clearValidationErrors());
                                      }}
                                    />
                                    {isLineItemFieldInError(index, "supplier_product_code") && (
                                      <FormHelperText error sx={{ fontSize: "0.7rem" }}>
                                        {getLineItemFieldError(index, "supplier_product_code")}
                                      </FormHelperText>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <TextField
                                      size="small"
                                      variant="standard"
                                      value={item.supplier_product_code}
                                      title={item.supplier_product_code}
                                      onChange={handleLineItemChange(index, "supplier_product_code")}
                                      error={!!isLineItemFieldInError(index, "supplier_product_code")}
                                      sx={{ minWidth: "100px" }}
                                      disabled={!editable}
                                      className="items_supplier_product_code"
                                    />
                                    {isLineItemFieldInError(index, "supplier_product_code") && (
                                      <FormHelperText error sx={{ fontSize: "0.7rem" }}>
                                        {getLineItemFieldError(index, "supplier_product_code")}
                                      </FormHelperText>
                                    )}
                                  </>
                                )}
                              </TableCell>
                              <TableCell>
                                {isManuallyAddedLineItem(item) ? (
                                  <>
                                    <Box sx={{ position: "relative" }}>
                                      <TextField
                                        size="small"
                                        variant="standard"
                                        value={item.product_name || ""}
                                        title={item.product_name + " | ID: " + item.product}
                                        placeholder="Select SPC"
                                        sx={{ minWidth: "150px", textTransform: "uppercase" }}
                                        disabled
                                        className="items_product_name"
                                      />
                                      <Box sx={{ position: "absolute", top: "25px", left: "5px", zIndex: 1, fontSize: "10px", color: "text.secondary" }}>
                                        {item.product ? `ID: ${item.product}` : "No product selected"}
                                      </Box>                                      
                                      {isLineItemFieldInError(index, "product_name") && (
                                        <FormHelperText error sx={{ fontSize: "0.7rem" }}>
                                          {getLineItemFieldError(index, "product_name")}
                                        </FormHelperText>
                                      )}                                 
                                    </Box>                                    
                                  </>
                                ) : (
                                  <Box sx={{ position: "relative" }}>
                                    <TextField
                                      size="small"
                                      variant="standard"
                                      value={item.product_name}
                                      title={item.product_name + " | ID: " + item.product}
                                      onChange={handleLineItemChange(index, "product_name")}
                                      sx={{ minWidth: "150px" }}
                                      disabled={!editable}
                                      className="items_product_name"
                                    />
                                    <Box sx={{ position: "absolute", top: "25px", left: "5px", zIndex: 1, fontSize: "10px", color: "text.secondary" }}>
                                      ID: {item.product}
                                    </Box>
                                  </Box>
                                )}
                              </TableCell>                              
                              {/* <TableCell>
                                {isManuallyAddedLineItem(item) ? (
                                  <>
                                    <ProductLookupCell
                                      value={item.product_name}
                                      productId={item.product}
                                      disabled={!editable}
                                      onSelect={({ id, name }) => {
                                        dispatch(
                                          updateLineItem({
                                            index,
                                            key: "product",
                                            value: id ? String(id) : null,
                                          })
                                        );
                                        dispatch(
                                          updateLineItem({
                                            index,
                                            key: "product_name",
                                            value: name,
                                          })
                                        );
                                        dispatch(clearValidationErrors());
                                      }}
                                    />
                                    {isLineItemFieldInError(index, 'product_name') && (
                                      <FormHelperText error sx={{ fontSize: '0.7rem' }}>
                                        {getLineItemFieldError(index, 'product_name')}
                                      </FormHelperText>
                                    )}
                                  </>
                                ) : (
                                  <TextField
                                    size="small"
                                    variant="standard"
                                    value={item.product_name}
                                    title={item.product_name}
                                    onChange={handleLineItemChange(
                                      index,
                                      "product_name",
                                    )}
                                    sx={{ minWidth: "150px" }}
                                    disabled={!editable}
                                    className="items_product_name"
                                  />
                                )}
                              </TableCell> */}
                              {/* <TableCell> */}
                              {/* <TextField
                                  size="small"
                                  variant="standard"
                                  value={item.product_name}
                                  title={item.product_name}
                                  onChange={handleLineItemChange(
                                    index,
                                    "product_name",
                                  )}
                                  // error={!!isLineItemFieldInError(index, 'product_name')}
                                  sx={{ minWidth: "150px" }}
                                  disabled={!editable}
                                  className="items_product_name"
                                /> */}
                              {/* {isLineItemFieldInError(index, 'product_name') && (
                                    <FormHelperText error sx={{fontSize: '0.7rem'}}>
                                        {getLineItemFieldError(index, 'product_name')}
                                    </FormHelperText>
                                )} */}
                              {/* </TableCell> */}
                              <TableCell>
                                <TextField
                                  size="small"
                                  variant="standard"
                                  type="text"
                                  value={item.quantity ?? ""}
                                  onFocus={() =>
                                    setEditingCell({
                                      row: index,
                                      key: "quantity",
                                    })
                                  }
                                  onBlur={(e) => {
                                    setEditingCell(null);
                                    dispatch(
                                      updateLineItem({
                                        index,
                                        key: "quantity",
                                        value: to2dpStr(e.target.value),
                                      }),
                                    );
                                  }}
                                  onChange={handleLineItemChange(
                                    index,
                                    "quantity",
                                  )}
                                  sx={{ width: "60px" }}
                                  // error={!!isLineItemFieldInError(index, 'quantity')}
                                  disabled={!editable}
                                />
                                {/* {isLineItemFieldInError(index, 'quantity') && (
                                      <FormHelperText error sx={{fontSize: '0.7rem'}}>
                                          {getLineItemFieldError(index, 'quantity')}
                                      </FormHelperText>
                                  )} */}
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  variant="standard"
                                  type="text"
                                  value={item.unit_price ?? ""}
                                  onFocus={() =>
                                    setEditingCell({
                                      row: index,
                                      key: "unit_price",
                                    })
                                  }
                                  onBlur={(e) => {
                                    setEditingCell(null);
                                    dispatch(
                                      updateLineItem({
                                        index,
                                        key: "unit_price",
                                        value: to2dpStr(e.target.value),
                                      }),
                                    );
                                  }}
                                  onChange={handleLineItemChange(
                                    index,
                                    "unit_price",
                                  )}
                                  sx={{ width: "80px" }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        £
                                      </InputAdornment>
                                    ),
                                  }}
                                  error={
                                    !!isLineItemFieldInError(
                                      index,
                                      "unit_price",
                                    )
                                  }
                                  disabled={!editable}
                                />
                                {isLineItemFieldInError(
                                  index,
                                  "unit_price",
                                ) && (
                                    <FormHelperText
                                      error
                                      sx={{ fontSize: "0.7rem" }}
                                    >
                                      {getLineItemFieldError(index, "unit_price")}
                                    </FormHelperText>
                                  )}
                              </TableCell>
                              {/* <TableCell>
                                  <TextField 
                                      size="small" 
                                      variant="standard" 
                                      type="text"
                                      value={item.line_total ?? ''} 
                                      onFocus={() => setEditingCell({ row: index, key: 'line_total' })}
                                      onBlur={(e) => {
                                          setEditingCell(null);
                                          dispatch(updateLineItem({
                                          index,
                                          key: 'line_total',
                                          value: to2dpStr(e.target.value)
                                          }));
                                      }}
                                      onChange={handleLineItemChange(index, 'line_total')} 
                                      sx={{width: '110px'}} 
                                      InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                                      disabled={!editable}
                                  />
                              </TableCell> */}
                              <TableCell>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    type="text"
                                    value={item.line_vat_code ?? "0"}
                                    onFocus={() =>
                                      setEditingCell({
                                        row: index,
                                        key: "line_vat_code",
                                      })
                                    }
                                    onBlur={(e) => {
                                      setEditingCell(null);
                                      dispatch(
                                        updateLineItem({
                                          index,
                                          key: "line_vat_code",
                                          value:
                                            e.target.value === null
                                              ? null
                                              : typeof e.target.value ===
                                                "string"
                                                ? e.target.value
                                                : to2dpStr(e.target.value),
                                        }),
                                      );
                                    }}
                                    onChange={handleLineItemChange(
                                      index,
                                      "line_vat_code",
                                    )}
                                    sx={{ width: "60px" }}
                                    error={!!isLineItemFieldInError(index, "line_vat_code")}
                                    disabled={!editable}
                                  />
                                  {isLineItemFieldInError(index, "line_vat_code") && (
                                      <FormHelperText error sx={{ fontSize: "0.7rem" }}>
                                        {getLineItemFieldError(index, "line_vat_code")}
                                      </FormHelperText>
                                    )}                                  
                              </TableCell>                              
                              <TableCell>
                                  <TextField
                                    size="small"
                                    variant="standard"
                                    type="text"
                                    value={item.vat_code ?? "0"}
                                    onFocus={() =>
                                      setEditingCell({
                                        row: index,
                                        key: "vat_code",
                                      })
                                    }
                                    onBlur={(e) => {
                                      setEditingCell(null);
                                      dispatch(
                                        updateLineItem({
                                          index,
                                          key: "vat_code",
                                          value: to2dpStr(e.target.value),
                                        }),
                                      );
                                    }}
                                    onChange={handleLineItemChange(
                                      index,
                                      "vat_code",
                                    )}
                                    sx={{ width: "60px" }}
                                    error={!!isLineItemFieldInError(index, "vat_code")}
                                    disabled={!editable}
                                  />
                                  {isLineItemFieldInError(index, "vat_code") && (
                                      <FormHelperText error sx={{ fontSize: "0.7rem" }}>
                                        {getLineItemFieldError(index, "vat_code")}
                                      </FormHelperText>
                                    )}                                                                
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  variant="standard"
                                  type="text"
                                  value={item.vat_value ?? ""}
                                  onFocus={() =>
                                    setEditingCell({
                                      row: index,
                                      key: "vat_value",
                                    })
                                  }
                                  onBlur={(e) => {
                                    setEditingCell(null);
                                    dispatch(
                                      updateLineItem({
                                        index,
                                        key: "vat_value",
                                        value: to2dpStr(e.target.value),
                                      }),
                                    );
                                  }}
                                  onChange={handleLineItemChange(
                                    index,
                                    "vat_value",
                                  )}
                                  sx={{ width: "80px" }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        £
                                      </InputAdornment>
                                    ),
                                  }}
                                  disabled={!editable}
                                />
                                {/* <TextField
                                      size="small"
                                      variant="standard"
                                      type="text"
                                      value={item.vat_value}
                                      // onChange={handleLineItemChange(index, 'vat_value')}
                                      onChange={(e) => {
                                          setLastEditedVatField({
                                              index,
                                              field: "vat_value",
                                          });
                                          handleLineItemChange(index, "vat_value")(e);
                                      }}
                                      sx={{ width: "110px" }}
                                      InputProps={{
                                          startAdornment: (
                                              <InputAdornment position="start">
                                                  £
                                              </InputAdornment>
                                          ),
                                      }}
                                      disabled={!editable}
                                  /> */}
                              </TableCell>
                              {/* £{parseFloat(item.vat_value || 0).toFixed(2)} */}
                              {/* <TableCell>£{parseFloat(item.line_total || 0).toFixed(2)}</TableCell> */}
                              <TableCell>
                                <TextField
                                  size="small"
                                  variant="standard"
                                  type="text"
                                  value={item.line_total ?? ""}
                                  onFocus={() =>
                                    setEditingCell({
                                      row: index,
                                      key: "line_total",
                                    })
                                  }
                                  onBlur={(e) => {
                                    setEditingCell(null);
                                    dispatch(
                                      updateLineItem({
                                        index,
                                        key: "line_total",
                                        value: to2dpStr(e.target.value),
                                      }),
                                    );
                                  }}
                                  onChange={handleLineItemChange(
                                    index,
                                    "line_total",
                                  )}
                                  sx={{ width: "110px" }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        £
                                      </InputAdornment>
                                    ),
                                  }}
                                  disabled={!editable}
                                />
                              </TableCell>
                              {editable && (
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveLineItem(index)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {editable && (
                      <Box mt={2}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleAddLineItem}
                          startIcon={<AddIcon />}
                        >
                          Add Line Item
                        </Button>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>

              <Box mt="auto" pt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <FormLabel>Subtotal:</FormLabel>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="0.00"
                          disabled={!editable}
                          // 1. VALUE: Always bound to the local state. No more jumping UI.
                          value={localAmounts.subtotal}
                          // 2. ONCHANGE: Validate input to only allow valid currency format.
                          onChange={(e) => {
                            if (!editable) return;
                            const { value } = e.target;
                            // Regex allows numbers and an optional decimal point with up to 2 decimal places.
                            if (
                              value === "" ||
                              /^[0-9]*\.?[0-9]{0,2}$/.test(value)
                            ) {
                              setLocalAmounts((prev) => ({
                                ...prev,
                                subtotal: value,
                              }));
                            }
                          }}
                          // 3. ONBLUR: Format the value for display and dispatch a clean number to Redux.
                          onBlur={() => {
                            if (!editable) return;

                            const numericValue =
                              parseFloat(localAmounts.subtotal) || 0;

                            // Update local state to the clean, formatted version (e.g., "50" -> "50.00")
                            setLocalAmounts((prev) => ({
                              ...prev,
                              subtotal: numericValue.toFixed(2),
                            }));

                            // Dispatch the clean numeric value to your Redux store.
                            dispatch(
                              updateDocumentField({
                                section: "amount",
                                key: "subtotal",
                                value: numericValue, // Send a number, not a potentially dirty string
                              }),
                            );
                          }}
                          // 4. PRESENTATION: Use InputAdornment for a persistent currency symbol.
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                £
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormLabel>VAT Total:</FormLabel>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="0.00"
                          disabled={!editable}
                          // 1. VALUE: Directly bound to the local state for a smooth editing experience.
                          value={localAmounts.vat}
                          // 2. ONCHANGE: Validates user input, allowing only a valid number format.
                          onChange={(e) => {
                            if (!editable) return;
                            const { value } = e.target;
                            // Regex ensures only numbers with an optional decimal part (up to 2 places) can be entered.
                            if (
                              value === "" ||
                              /^[0-9]*\.?[0-9]{0,2}$/.test(value)
                            ) {
                              setLocalAmounts((prev) => ({
                                ...prev,
                                vat: value,
                              }));
                            }
                          }}
                          // 3. ONBLUR: Cleans, formats, and dispatches the final value.
                          onBlur={() => {
                            if (!editable) return;

                            const numericValue =
                              parseFloat(localAmounts.vat) || 0;

                            // Update local state to the formatted string (e.g., "12.5" -> "12.50")
                            setLocalAmounts((prev) => ({
                              ...prev,
                              vat: numericValue.toFixed(2),
                            }));

                            // Dispatch the clean numeric value to the Redux store.
                            dispatch(
                              updateDocumentField({
                                section: "amount",
                                key: "vat",
                                value: numericValue, // Dispatching a pure number is best practice
                              }),
                            );
                          }}
                          // 4. PRESENTATION: The "£" symbol is handled by InputAdornment.
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                £
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      {/* <Grid item xs={4}>
                        <FormLabel>Delivery Charge:</FormLabel>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="0.00"
                          disabled={!editable}

                          // 1. VALUE: Consistently bound to local state. No UI jumps.
                          value={localAmounts.shipping}

                          // 2. ONCHANGE: Validates input to allow only valid currency characters.
                          onChange={(e) => {
                            if (!editable) return;
                            const {value} = e.target;
                            // Regex allows numbers, an optional single decimal point, and up to 2 decimal places.
                            if (value === '' || /^[0-9]*\.?[0-9]{0,2}$/.test(value)) {
                              setLocalAmounts(prev => ({ ...prev, shipping: value }));
                            }
                          }}

                          // 3. ONBLUR: Formats the value and dispatches a clean number to Redux.
                          onBlur={() => {
                            if (!editable) return;
                            
                            const numericValue = parseFloat(localAmounts.shipping) || 0;
                            
                            // Update local state with the formatted value for display consistency.
                            setLocalAmounts(prev => ({ ...prev, shipping: numericValue.toFixed(2) }));
                            
                            // Dispatch the clean numeric value to the Redux store.
                            dispatch(updateDocumentField({ 
                              section: 'amount', 
                              key: 'shipping', 
                              value: numericValue // Send a clean number, not a raw string.
                            }));
                          }}

                          // 4. PRESENTATION: Uses InputAdornment for the currency symbol.
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                £
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid> */}
                      <Grid item xs={4}>
                        <FormLabel>Total Amount:</FormLabel>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="0.00"
                          disabled={!editable}
                          sx={{
                            "& .MuiInputBase-input": { fontWeight: "bold" },
                          }}
                          // 1. VALUE: Always comes from the same local state. No more conditional logic.
                          value={localAmounts.total}
                          // 2. ONCHANGE: Validate input to only allow numbers and one decimal point.
                          onChange={(e) => {
                            if (!editable) return;
                            const { value } = e.target;
                            // Regex allows numbers, an optional single decimal point, and more numbers
                            if (
                              value === "" ||
                              /^[0-9]*\.?[0-9]{0,2}$/.test(value)
                            ) {
                              setLocalAmounts((prev) => ({
                                ...prev,
                                total: value,
                              }));
                            }
                          }}
                          // 3. ONBLUR: Format the value and dispatch a clean NUMBER to the store.
                          onBlur={() => {
                            if (!editable) return;

                            const numericValue =
                              parseFloat(localAmounts.total) || 0;

                            // Update local state to the clean, formatted version for display
                            setLocalAmounts((prev) => ({
                              ...prev,
                              total: numericValue.toFixed(2),
                            }));

                            // Dispatch the clean numeric value to Redux
                            dispatch(
                              updateDocumentField({
                                section: "amount",
                                key: "total_including_vat_tax_discounts",
                                value: numericValue, // Dispatching a number is cleaner
                              }),
                            );
                          }}
                          // 4. PRESENTATION: Use InputAdornment for the currency symbol.
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                £
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Status-based Button Visibility */}
                  {(visibleButtons.reject ||
                    visibleButtons.save ||
                    visibleButtons.process ||
                    visibleButtons.archive) && (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "10px",
                        }}
                      >
                        {visibleButtons.reject && (
                          <LoadingButton
                            variant="contained"
                            color="warning"
                            onClick={handleReject}
                            loading={isLoading}
                            disabled={isLoading}
                          >
                            Reject
                          </LoadingButton>
                        )}
                        {visibleButtons.save && (
                          <LoadingButton
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            loading={isLoading}
                            disabled={isLoading}
                          >
                            Save Record
                          </LoadingButton>
                        )}
                        {visibleButtons.process && (
                          <ProcessButton
                            variant="contained"
                            onClick={handleProcess}
                            loading={isLoading}
                            disabled={isLoading}
                          >
                            Process Record
                          </ProcessButton>
                        )}
                        {visibleButtons.archive && (
                          <LoadingButton
                            variant="contained"
                            color="secondary"
                            onClick={handleArchive}
                            loading={isLoading}
                            disabled={isLoading}
                          >
                            Archive
                          </LoadingButton>
                        )}
                      </Grid>
                    )}
                </Grid>
              </Box>
            </Box>

            <Box
              sx={{
                width: "45%",
                backgroundColor: "#f0f2f5",
                borderLeft: "1px solid #e0e0e0",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }}>
                <Typography
                  variant="h6"
                  sx={{ fontSize: "1rem", fontWeight: "400" }}
                >
                  Document Preview - {getFileNameFromUrl(document.file_urls)}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {document.file_urls ? (
                  <Paper
                    elevation={3}
                    sx={{ width: "100%", height: "100%", overflow: "hidden" }}
                  >
                    <iframe
                      src={document.file_urls}
                      title="Document Preview"
                      width="100%"
                      height="100%"
                      style={{ border: "none" }}
                    />
                  </Paper>
                ) : (
                  <Paper
                    sx={{
                      width: "80%",
                      height: "80%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#e0e0e0",
                    }}
                  >
                    <Typography color="text.secondary">
                      No preview available
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>
          </Box>
        </StyledModal>
      </Modal>

      {/* Reject Confirmation Modal */}
      <RejectConfirmationModal
        open={showRejectModal}
        onClose={handleRejectCancel}
        onConfirm={handleRejectConfirm}
        loading={isLoading}
      />

      {/* Delete Line Item Confirmation Modal */}
      <DeleteLineItemConfirmationModal
        open={showDeleteLineItemModal}
        onClose={handleDeleteLineItemCancel}
        onConfirm={handleDeleteLineItemConfirm}
        loading={isLoading}
      />

      {snackBar.show && (
        <SnackBarDialog
          onShow={snackBar.show}
          text={snackBar.text}
          type={snackBar.type}
          onHide={() => setSnackBar({ ...snackBar, show: false })}
        />
      )}
    </LocalizationProvider>
  );
}

EditViewDocumentProcess.propTypes = {
  onShow: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default EditViewDocumentProcess;
